import { conform, useForm } from '@conform-to/react'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import {
	json,
	redirect,
	type DataFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Form, Link, useActionData, useSearchParams } from '@remix-run/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '~/components/error-boundary.tsx'
import { ErrorList, Field } from '~/components/forms.tsx'
import { StatusButton } from '~/components/ui/status-button.tsx'
import { prepareVerification } from '~/routes/resources+/verify.tsx'
import { prisma } from '~/utils/db.server.ts'
import { sendEmail } from '~/utils/email.server.ts'
import { useIsSubmitting } from '~/utils/misc.ts'
import { emailSchema } from '~/utils/user-validation.ts'
import { SignupEmail } from './email.server.tsx'
import { useRecaptcha } from '~/components/recaptcha-provider.tsx'
import {
	RECAPTCHA_REFUSED,
	shouldRefuseForCaptcha,
} from '~/utils/recaptcha.server.ts'

export const onboardingOTPQueryParam = 'code'
export const onboardingEmailQueryParam = 'email'
export const verificationType = 'onboarding'

export const SignupSchema = z.object({
	email: emailSchema,
})

export async function action({ request }: DataFunctionArgs) {
	const formData = await request.formData()
	const submission = await parse(formData, {
		schema: SignupSchema.superRefine(async (data, ctx) => {
			const existingUser = await prisma.user.findUnique({
				where: { email: data.email },
				select: { id: true },
			})
			if (existingUser) {
				ctx.addIssue({
					path: ['email'],
					code: z.ZodIssueCode.custom,
					message: 'A user already exists with this email',
				})
				return
			}
		}),
		acceptMultipleErrors: () => true,
		async: true,
	})
	// 0.5, not 0.8. This form sits between the Reverse Recruiter's five
	// questions and its card screen, and in-app browsers (LinkedIn, Instagram,
	// Gmail) routinely score under 0.8 while being entirely real people.
	if (
		await shouldRefuseForCaptcha(formData.get('_captcha'), {
			action: 'signup',
			threshold: 0.5,
		})
	) {
		submission.error[''] = [RECAPTCHA_REFUSED]
		return json({ status: 'error', submission } as const, { status: 401 })
	}
	if (submission.intent !== 'submit') {
		return json({ status: 'idle', submission } as const)
	}
	if (!submission.value) {
		return json({ status: 'error', submission } as const, { status: 400 })
	}
	const { email } = submission.value
	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'onboarding',
		target: email,
	})

	const response = await sendEmail({
		to: email,
		subject: `Welcome to Resume Tailor!`,
		react: <SignupEmail onboardingUrl={verifyUrl.toString()} otp={otp} />,
	})

	if (response.status === 'success') {
		return redirect(redirectTo.toString())
	} else {
		submission.error[''] = response.error.message
		return json({ status: 'error', submission } as const, { status: 500 })
	}
}

export const meta: MetaFunction = () => {
	return [
		{ title: 'Sign Up | Resume Tailor' },
		{
			name: 'description',
			content: 'Land more job interviews.',
		},
	]
}

export default function SignupRoute() {
	const actionData = useActionData<typeof action>()
	const [searchParams] = useSearchParams()
	const isSubmitting = useIsSubmitting()
	// Cold traffic contains people who already have an account. Without a way
	// across, "a user already exists with this email" is a dead end, and the
	// login route only reads redirectTo off the query string.
	const redirectTo = searchParams.get('redirectTo')
	const loginHref = redirectTo
		? `/login?redirectTo=${encodeURIComponent(redirectTo)}`
		: '/login'
	const [form, fields] = useForm({
		id: 'signup-form',
		constraint: getFieldsetConstraint(SignupSchema),
		lastSubmission: actionData?.submission,
		onValidate({ formData }) {
			const result = parse(formData, { schema: SignupSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
	})

	const { token, status } = useRecaptcha('signup')

	return (
		<div className="flex flex-col justify-center pb-32 pt-20 md:container">
			<div className="text-center">
				<h1 className="text-h1">Let's start your journey!</h1>
				<p className="mt-3 text-body-md text-muted-foreground">
					Please enter your email.
				</p>
			</div>
			<Form
				method="POST"
				className="mx-auto mt-16 min-w-[368px] max-w-sm"
				{...form.props}
			>
				{token ? <input type="hidden" name="_captcha" value={token} /> : null}
				<Field
					labelProps={{
						htmlFor: fields.email.id,
						children: 'Email',
					}}
					inputProps={{ ...conform.input(fields.email), autoFocus: true }}
					errors={fields.email.errors}
				/>
				<ErrorList errors={form.errors} id={form.errorId} />
				<StatusButton
					className="w-full"
					status={isSubmitting ? 'pending' : actionData?.status ?? 'idle'}
					type="submit"
					// Gate on the captcha having SETTLED, not on a token existing.
					// `!token` meant a visitor whose browser Google will not assess —
					// Brave with shields up, uBlock, an in-app webview, a network that
					// blocks google.com — had this button disabled forever, with no
					// error text anywhere on screen. That is the exact person the
					// server was deliberately loosened to let through
					// (shouldRefuseForCaptcha allows a missing token, backstopped by
					// the 10/min per-IP limit), so the leniency was unreachable from a
					// browser and the funnel still died silently.
					disabled={isSubmitting || (!token && status !== 'failed')}
				>
					Submit
				</StatusButton>
				<p className="mt-4 text-center text-body-xs text-muted-foreground">
					Already have an account?{' '}
					<Link className="underline" to={loginHref}>
						Log in
					</Link>
				</p>
			</Form>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
